/**
 * Finance → Company earnings, for today, this week, this month, this year or a range.
 *
 *   Delivery commission + Remittance received + Other revenue = Total company revenue
 *   Total company revenue − Maintenance      = Earnings after maintenance
 *
 * "Earnings after maintenance", never "net profit": rent, salaries and every
 * other expense are not deducted here, and a label that suggested they were
 * would be read as the bottom line.
 *
 * Commission is counted once, from the deliveries; the riders' share of the
 * fees is never company revenue. Remittance counts when it is PAID, on the
 * day it arrived: a week charged and unpaid adds nothing, and its charge is
 * shown beside the figures as "charged", never added to them. Customers paying
 * for rides late are collections: cash on the day it came, not a second
 * revenue entry. Every figure opens onto the transactions behind it.
 */

import { useState, type ReactNode } from 'react';
import type { CompanyEarningsFigures, CompanyEarningsKind, CompanyEarningsPeriod } from '@cenox/shared';
import { Card, EmptyState, ErrorState, Input, Modal, TableSkeleton, cn } from '../../components/ui';
import { RemittanceDistributionReport } from './RemittanceDistributionReport';
import { LocationScope } from '../../components/LocationScope';
import { useLocationFilter, useLocationReady, type ScopeFilter } from '../../lib/locationContext';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { useCompanyEarnings, useCompanyEarningsTransactions } from './data';

const PERIODS: Array<{ value: CompanyEarningsPeriod; label: string }> = [
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
];

const KIND_LABELS: Record<CompanyEarningsKind, string> = {
  commission: 'Delivery commission',
  other_revenue: 'Other revenue',
  operating_expenses: 'Operating expenses',
  remittance: 'Remittance revenue (received)',
  remittance_charged: 'Remittance charged (accrued)',
  remittance_outstanding: 'Remittance outstanding',
  maintenance: 'Maintenance expenses',
  remittance_collections: 'Remittance received',
  delivery_collections: 'Delivery fees received',
};

export function CompanyEarningsPage(): ReactNode {
  const { business, can } = useAuth();
  const today = todayIn(business?.timezone ?? 'Africa/Lagos');
  const [period, setPeriod] = useState<CompanyEarningsPeriod>('month');
  const [from, setFrom] = useState(`${today.slice(0, 7)}-01`);
  const [to, setTo] = useState(today);
  const [open, setOpen] = useState<{ kind: CompanyEarningsKind; currency: string } | null>(null);

  // The sidebar's Country → State/Region → Location, as on every screen.
  const scope = useLocationFilter();
  const scopeReady = useLocationReady();
  const earnings = useCompanyEarnings({ period, from, to, scope, scopeReady });
  const range = earnings.data ? { from: earnings.data.from, to: earnings.data.to, scope } : { from, to, scope };

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Company earnings</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Delivery commission, remittance and other revenue, less maintenance and then operating expenses.
          Riders&apos; pay is never deducted twice. Before tax, loan bad debts and asset costs.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div role="tablist" aria-label="Period" className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              role="tab"
              aria-selected={period === p.value}
              onClick={() => setPeriod(p.value)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium',
                period === p.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        {period === 'custom' ? (
          <div className="flex gap-2">
            <div className="w-40">
              <Input label="From" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="w-40">
              <Input label="To" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
        ) : null}
        {earnings.data ? (
          <p className="pb-2 text-sm text-slate-500">
            {earnings.data.from} → {earnings.data.to}
          </p>
        ) : null}
      </div>

      <LocationScope testId="finance-scope" />

      {earnings.isError ? (
        <ErrorState message="Could not load company earnings." onRetry={() => void earnings.refetch()} />
      ) : earnings.isPending ? (
        <TableSkeleton rows={3} columns={5} />
      ) : earnings.data.currencies.length === 0 ? (
        <EmptyState title="Nothing in this period" description="No commission, remittance or maintenance was recorded." />
      ) : (
        earnings.data.currencies.map((c) => (
          <CurrencyBlock
            key={c.currencyCode}
            figures={c}
            showCode={earnings.data.currencies.length > 1}
            onOpen={(kind) => setOpen({ kind, currency: c.currencyCode })}
          />
        ))
      )}

      {earnings.data?.remittanceNote ? <p className="text-xs text-slate-500">{earnings.data.remittanceNote}</p> : null}

      <TransactionsModal target={open} range={range} onClose={() => setOpen(null)} />

      {can('remittance:manage') && earnings.data && earnings.data.remittanceNote === null ? (
        <RemittanceDistributionReport from={earnings.data.from} to={earnings.data.to} />
      ) : null}
    </div>
  );
}

function Tile({
  label,
  value,
  currency,
  hint,
  tone = 'plain',
  onOpen,
}: {
  label: string;
  value: string | null;
  currency: string;
  hint?: string;
  tone?: 'plain' | 'good' | 'cost' | 'total';
  onOpen?: () => void;
}): ReactNode {
  const body = (
    <>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p
        className={cn(
          'mt-1 text-lg font-semibold tabular-nums',
          tone === 'good' && 'text-emerald-700',
          tone === 'cost' && 'text-rose-700',
          tone === 'total' && 'text-slate-900',
          tone === 'plain' && 'text-slate-900',
        )}
      >
        {value === null ? '—' : money(value, currency)}
      </p>
      {hint ? <p className="mt-0.5 text-xs text-slate-400">{hint}</p> : null}
    </>
  );
  const box = cn(
    'rounded-xl border bg-white p-3.5 text-left',
    tone === 'total' ? 'border-slate-300' : 'border-slate-200',
  );
  return onOpen && value !== null ? (
    <button type="button" onClick={onOpen} className={cn(box, 'hover:border-brand-300 hover:bg-slate-50')}>
      {body}
    </button>
  ) : (
    <div className={box}>{body}</div>
  );
}

function CurrencyBlock({
  figures: f,
  showCode,
  onOpen,
}: {
  figures: CompanyEarningsFigures;
  showCode: boolean;
  onOpen: (kind: CompanyEarningsKind) => void;
}): ReactNode {
  const cur = f.currencyCode;
  return (
    <Card>
      {showCode ? <h2 className="mb-3 text-sm font-semibold text-slate-900">{cur}</h2> : null}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Delivery commission" value={f.deliveryCommission} currency={cur} tone="good" onOpen={() => onOpen('commission')} hint="Company share only" />
        <Tile label="Remittance revenue" value={f.remittanceRevenue} currency={cur} tone="good" onOpen={() => onOpen('remittance')} hint="Company share received, by payment date" />
        <Tile label="Other revenue" value={f.otherRevenue} currency={cur} tone="good" onOpen={() => onOpen('other_revenue')} hint="Other sales, loan interest and fees" />
        <Tile label="Total company revenue" value={f.totalCompanyRevenue} currency={cur} tone="total" hint="Commission + remittance + other" />
        <Tile label="Maintenance expenses" value={f.maintenanceExpenses} currency={cur} tone="cost" onOpen={() => onOpen('maintenance')} />
        <Tile label="Earnings after maintenance" value={f.earningsAfterMaintenance} currency={cur} tone="total" hint="Revenue − maintenance" />
        <Tile label="Operating expenses" value={f.operatingExpenses} currency={cur} tone="cost" onOpen={() => onOpen('operating_expenses')} hint="Riders' pay excluded: already outside commission" />
        <Tile
          label="Earnings after expenses"
          value={f.earningsAfterExpenses}
          currency={cur}
          tone="total"
          hint="Before tax, loan bad debts and asset costs"
        />
      </div>

      {Number(f.commissionAwaitingPayment) > 0 ? (
        <p className="mt-3 text-xs text-slate-500">
          Not counted above: {money(f.commissionAwaitingPayment, cur)} of commission on completed rides the
          customer has not paid for yet. It counts on the ride&apos;s own day once paid.
        </p>
      ) : null}

      <h3 className="mb-2 mt-5 text-sm font-semibold text-slate-900">Collections in this period</h3>
      <p className="mb-3 text-xs text-slate-500">
        Money that arrived in these dates, whichever week or ride it paid for. Collections are cash, not
        revenue — a late payment is counted here on the day it came, and never as revenue twice.
      </p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Tile label="Remittance received" value={f.collections.remittanceReceived} currency={cur} onOpen={() => onOpen('remittance_collections')} />
        <Tile label="Delivery fees received" value={f.collections.deliveryFeesReceived} currency={cur} onOpen={() => onOpen('delivery_collections')} />
      </div>

      {f.grossRemittanceCollected !== null && (Number(f.investorFundsCollected) > 0 || Number(f.investorPayablesOutstanding) > 0 || Number(f.investorFundsPaidOut) > 0) ? (
        <>
          <h3 className="mb-2 mt-5 text-sm font-semibold text-slate-900">Remittance: company and investors</h3>
          <p className="mb-3 text-xs text-slate-500">
            On an investor&apos;s bike, part of each week&apos;s remittance is the investor&apos;s. It is collected
            for them and owed to them — never company revenue — and paying it out settles what is owed, so it is
            never an expense either.
          </p>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Tile label="Gross remittance collected" value={f.grossRemittanceCollected} currency={cur} onOpen={() => onOpen('remittance_collections')} hint="All of it, investors' share included" />
            <Tile label="Company remittance revenue" value={f.remittanceRevenue} currency={cur} tone="good" onOpen={() => onOpen('remittance')} />
            <Tile label="Investor funds collected" value={f.investorFundsCollected} currency={cur} hint="Owed to investors" />
            <Tile label="Investor funds paid out" value={f.investorFundsPaidOut} currency={cur} hint="In these dates" />
            <Tile label="Outstanding investor payables" value={f.investorPayablesOutstanding} currency={cur} tone="cost" hint="At the end of the period" />
          </div>
        </>
      ) : null}

      {f.remittanceCharged !== null ? (
        <>
          <h3 className="mb-2 mt-5 text-sm font-semibold text-slate-900">Remittance charged for weeks ending in these dates</h3>
          <p className="mb-3 text-xs text-slate-500">
            What riders were charged, and what they still owe on those weeks today. Balances, not revenue: remittance
            counts as revenue only when it is received, above.
          </p>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Tile label="Remittance charged" value={f.remittanceCharged} currency={cur} onOpen={() => onOpen('remittance_charged')} hint="Accrued, waived weeks excluded" />
            <Tile label="Remittance outstanding" value={f.remittanceOutstanding} currency={cur} tone="cost" onOpen={() => onOpen('remittance_outstanding')} hint="Still owed on those weeks" />
          </div>
        </>
      ) : null}
    </Card>
  );
}

function TransactionsModal({
  target,
  range,
  onClose,
}: {
  target: { kind: CompanyEarningsKind; currency: string } | null;
  range: { from: string; to: string; scope?: ScopeFilter | undefined };
  onClose: () => void;
}): ReactNode {
  const list = useCompanyEarningsTransactions(target, range);
  return (
    <Modal open={target !== null} onClose={onClose} title={target ? KIND_LABELS[target.kind] : ''} size="lg">
      {list.isError ? (
        <ErrorState message="Could not load the transactions." onRetry={() => void list.refetch()} />
      ) : list.isPending ? (
        <TableSkeleton rows={4} columns={4} />
      ) : (
        <>
          <p className="mb-2 text-sm text-slate-600">
            {list.data.count} {list.data.count === 1 ? 'entry' : 'entries'}, totalling{' '}
            <strong>{money(list.data.total, list.data.currencyCode)}</strong>
            {list.data.truncated ? ' — the first 1,000 are listed; the total covers them all.' : '.'}
          </p>
          <div className="-mx-3 max-h-[60vh] overflow-auto px-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 pr-3 font-medium">Reference</th>
                  <th className="py-2 pr-3 font-medium">Detail</th>
                  <th className="py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.data.rows.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap py-2 pr-3 text-slate-700">{r.date}</td>
                    <td className="py-2 pr-3 text-slate-500">{r.reference ?? '—'}</td>
                    <td className="py-2 pr-3 text-slate-700">
                      {r.description}
                      {r.counterparty ? <span className="block text-xs text-slate-400">{r.counterparty}</span> : null}
                    </td>
                    <td className="py-2 text-right tabular-nums">{money(r.amount, list.data.currencyCode)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
